<template>
  <div v-if="dataLoaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn class="capitalize ctrl-button" color="grey-5" icon="keyboard_backspace"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/magazine/${$route.params.magazine}`)" />
      </div>

      <div class="col-6">
        <q-btn class="capitalize ctrl-button right q-ml-md" :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
          :label="readOnly ? getString('action.edit') : getString('action.save')" @click="saveChanges()"
          :icon-right="readOnly ? 'edit' : 'save'" />
        <q-btn v-if="data.id" class="q-ml-md capitalize ctrl-button right" icon-right="delete" color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete" @click="deleteItem()" align="right" />
      </div>
    </div>

    <div class="row gutter-sm q-mb-lg">
      <!-- General Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString(null, 'Details', 'Details') }}
        </p>
        <div class="card-block">
          <!-- Name -->
          <q-field class="q-mb-md" :error="$v.data.name.$dirty && !$v.data.name.required"
            :error-label="getString(null, 'Name is required', 'Naam is verplicht')">
            <q-input type="text" @input="suggestUrl" v-model.trim="$v.data.name.$model" :disable="readOnly"
              :float-label="getString('name')">
            </q-input>
          </q-field>
          <q-field class="q-mb-md" :error="$v.data.url.$dirty && !$v.data.url.required"
            :error-label="getString(null, 'Url is required', 'Url is verplicht')">
            <q-input type="text" v-model="$v.data.url.$model" :disable="readOnly" :float-label="getString('url')">
            </q-input>
          </q-field>
          <!-- ISBN -->
          <q-field class="q-mb-md" :error="$v.data.isbn.$dirty && $v.data.isbn.$anyError"
            :error-label="!$v.data.isbn.required ?
              getString(null, 'ISBN is required', 'ISBN is verplicht') :
              getString(null, 'Invalid ISBN', 'Geen geldige ISBN')">
            <q-input type="text" @blur="$v.data.isbn.$touch()" v-model.lazy="data.isbn"
              :disable="readOnly" float-label="ISBN">
            </q-input>
          </q-field>
        </div>
      </div>

      <div class="col-md-6 col-sm-12 col-xs-12">
        <!-- General Information -->
        <div class="col-md-6 col-sm-12 col-xs-12">
          <p class="card-title">
            {{ getString(null, 'Image', 'Afbeelding') }}
          </p>
          <div class="card-block">
            <!-- Image -->
            <div class="row">
              <div class="col-md-8 q-mr-md">
                <div class="card-block image-preview image flex-center">
                  <img v-if="data.image && data.image.location" class="image-preview__small centered"
                    :src="`${buildFileLocation(data.image.location)}`" />
                </div>
              </div>
              <div class="col">
                <q-btn class="capitalize ctrl-button" :color="readOnly ? 'grey-5' : 'primary'" :disabled="readOnly"
                  :label="getString('editor.button.image.select')" @click="openImageModal" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Content editor -->
    <div class="row gutter-sm q-mb-lg q-mt-sm">
      <div class="full-width">
        <p class="card-title">{{ getString('block.type.html.content') }}</p>
        <div class="card-block">

          <!-- Field: HTML Editor -->
          <tiny-editor
            tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
            :init="tinyConfig()"
            ref="tm"
            :disabled="readOnly"
            v-model="data.description">
          </tiny-editor>
        </div>
      </div>
    </div>
    <!-- image select modal -->
    <q-modal v-if="imageModal" v-model="imageModal">
      <file-select-modal @close="imageModal = false" fileType="image" :guid="guid" />
    </q-modal>
  </div>
</template>

<script>
/* eslint-disable no-useless-escape */
import { required, helpers } from 'vuelidate/lib/validators';
import saveChanges from '@/mixins/detailViews/saveChanges';
import suggestUrl from '@/mixins/suggestUrl';
import getString from '@/helpers/GetStrings';
import FileSelectModal from '@/components/modal/FileSelectModal.vue';
import EventBus from '@/event-bus';

const TinyEditor = require('@tinymce/tinymce-vue').default;

const isbnRegex = helpers.regex('isbn', /^(?:ISBN(?:-13)?:?\ )?(?=[0-9]{13}$|(?=(?:[0-9]+[-\ ]){4})[-\ 0-9]{17}$)97[89][-\ ]?[0-9]{1,5}[-\ ]?[0-9]+[-\ ]?[0-9]+[-\ ]?[0-9]$/);

const isbnChecksum = (subject) => {
  // Remove non ISBN digits, then split into an array
  const chars = subject.replace(/[- ]|^ISBN(?:-1[03])?:?/g, '').split('');
  // Remove the final ISBN digit from `chars`, and assign it to `last`
  const last = chars.pop();
  let sum = 0;
  let check; let
    i;

  if (chars.length === 9) {
    // Compute the ISBN-10 check digit
    chars.reverse();
    for (i = 0; i < chars.length; i += 1) {
      sum += (i + 2) * parseInt(chars[i], 10);
    }
    check = 11 - (sum % 11);
    if (Number(check) === 10) {
      check = 'X';
    } else if (Number(check) === 11) {
      check = '0';
    }
  } else {
    // Compute the ISBN-13 check digit
    for (i = 0; i < chars.length; i += 1) {
      sum += ((i % 2) * 2 + 1) * parseInt(chars[i], 10);
    }
    check = 10 - (sum % 10);
    if (Number(check) === 10) {
      check = '0';
    }
  }

  return Number(check) === Number(last);
};

export default {
  validations: {
    data: {
      name: {
        required,
      },
      url: {
        required,
      },
      isbn: {
        isbnRegex,
        isbnChecksum,
        required,
      },
    },
  },
  data() {
    return {
      guid: null,
      successMessage: this.getString(null, 'The edition has been saved', 'Het uitgave is succesvol opgeslagen'),
      readOnly: true,
      imageModal: false,
      noCategory: null,
      categories: [],
      dataLoaded: false,
      responseData: {
        multiple: 'magazines',
        single: 'magazine',
      },
      module: 'magazine',
      data: {
        id: null,
        image: {},
        name: '',
        description: '',
        url: '',
        isbn: '',
      },
    };
  },

  components: {
    TinyEditor,
    FileSelectModal,
  },

  mixins: [suggestUrl, saveChanges],

  computed: {
    endpoint() {
      return `magazine/${this.$route.params.magazine}/edition`;
    },
  },
  methods: {
    getString,

    openImageModal() {
      this.guid = 'image';
      this.imageModal = true;
    },

    tinyConfig() {
      return {
        branding: false,
        browser_spellcheck: true,
        plugins: this.plugins,
        toolbar: [
          'undo redo | styleselect | bold italic | link image alignleft aligncenter alignright | code',
        ],
        templates: [
          { title: 'Name', description: 'Users full name', content: `${this.$store.state.meStore.me.name.firstname} ${this.$store.state.meStore.me.name.surname}` },
        ],
        pagebreak_split_block: true,
      };
    },

    // Takes full location as an argument and returns the nested upload folder location
    buildFileLocation(location) {
      if (!location) {
        return null;
      }
      return `${process.env.VUE_APP_API_URL}${location.replace('api/library', 'library/download')}`;
    },

    requestParams() {
      const requiredFields = {
        name: this.data.name,
        url: this.data.url,
        isbn: this.data.isbn,
      };

      const optional = {
        description: this.data.description,
        image: this.data.image && this.data.image.location && this.data.image.location.replace('api/', ''),
      };

      // If a value is null, undefined or empty, remove it from fields
      for (const [key, value] of Object.entries(optional)) {
        if (value === null || value === undefined) delete optional[key];
      }

      // Join required and optional
      const fields = Object.assign(requiredFields, optional);

      return fields;
    },

    deleteItem() {
      // Dialog
      this.$q
        .dialog({
          title: '',
          message: this.getString('module.library.delete.title.single'),
          ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
          cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
        })
        .then(() => {
          // delete call
          this.axios
            .delete(`magazine/${this.$route.params.magazine}/edition/${this.$route.params.id}`)
            .then(() => {
              // Refresh data to see the result
              this.$q.notify({
                message: this.getString('upload.delete.success').replace(
                  '%file%',
                  'edition',
                ),
                position: 'top-right',
                timeout: 1000,
                color: 'positive',
              });
              this.$router.push(`/magazine/${this.$route.params.magazine}`);
            })
            .catch((error) => {
              // Error
              console.warn(error);
            });
        })
        .catch(() => {
          console.warn('Not removed');
        });
    },
  },

  mounted() {
    if (this.$route.params.id) this.readOnly = true;

    EventBus.$on('file-select:image', (data) => {
      if (data.type === 'image') {
        this.imageSelected = true;
        this.imgSrc = data.location;
        if (this.data.image) {
          this.data.image.location = data.location;
        } else {
          // Added in case of an incoming post where image in null
          this.data.image = { location: data.location };
        }
      }

      this.data.cover = data;

      this.imageModal = false;
      this.fileModal = false;
    });
  },

  created() {
    if (this.$route.params.id) {
      // Fetch the selected module details by id.
      this.axios
        .get(`magazine/${this.$route.params.magazine}/edition/${this.$route.params.id}`)
        .then((response) => {
          // In the module detail page, if a given id is not found, we will always get a 200 OK response.
          if (!response.data.edition) {
            // Redirect to modules overview
            this.$router.push(`/${this.endpoint}`);
            return;
          }

          this.data = response.data.edition;

          this.dataLoaded = true;
        })
        .catch((error) => {
          console.warn(error);

          // Notify if nothing found
          if (error.response.status === 404) {
            this.$q.notify({
              message: getString(`module.${this.module}.edit.error.nothingFound`),
              type: 'warning',
              timeout: 2000,
              position: 'top-right',
            });
          } else {
            // Throw general error notification
            this.$q.notify({
              message: getString('error.general'),
              position: 'top-right',
              timeout: 1000,
              type: 'warning',
            });
          }

          // Redirect to modules overview
          this.$router.push(`/${this.module}`);
        });
    } else {
      // Nothing to load
      this.dataLoaded = true;
      this.readOnly = false;
    }
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
.data-title
  font-weight 700

.data-content
  word-break break-all
.image-preview.image {
  padding: 0;
  display flex;
  height: 200px;
  background-image: linear-gradient(45deg, #F3F3F3 25%, transparent 5%), linear-gradient(-45deg, #F3F3F3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F3F3F3 75%), linear-gradient(-45deg, transparent 75%, #F3F3F3 75%);
}
.centered {
  text-align: center;
  display: block;
  margin-left: auto;
  margin-right: auto;
}
.image-preview {
  height: 40vh;
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  position: relative;

  img {
    max-width: 100%;
    max-height: 100%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}

</style>
